import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { $getSelection, $isRangeSelection } from 'lexical';
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getTableCellNodeFromLexicalNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableSelection,
  TableCellNode,
} from '@lexical/table';
import { Button } from 'primereact/button';
import { richTextEditorStrings } from '../../../i18n/components/richtTextEditor';

const PRESET_COLORS = [
  {
    name: richTextEditorStrings.colorPicker.presetColors.white,
    value: '#ffffff',
  },
  {
    name: richTextEditorStrings.colorPicker.presetColors.lightGray,
    value: '#f3f4f6',
  },
  {
    name: richTextEditorStrings.colorPicker.presetColors.lightBlue,
    value: '#dbeafe',
  },
  {
    name: richTextEditorStrings.colorPicker.presetColors.lightGreen,
    value: '#dcfce7',
  },
  {
    name: richTextEditorStrings.colorPicker.presetColors.lightYellow,
    value: '#fef3c7',
  },
  {
    name: richTextEditorStrings.colorPicker.presetColors.lightRed,
    value: '#fee2e2',
  },
  {
    name: richTextEditorStrings.colorPicker.presetColors.lightPurple,
    value: '#ede9fe',
  },
  {
    name: richTextEditorStrings.colorPicker.presetColors.lightPink,
    value: '#fce7f3',
  },
];

export function TableActionMenuPlugin() {
  const [editor] = useLexicalComposerContext();
  const [tableCellNode, setTableCellNode] = useState<TableCellNode | null>(
    null,
  );
  const [selectedCells, setSelectedCells] = useState<TableCellNode[]>([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerButtonRef = useRef<HTMLButtonElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [colorPickerPosition, setColorPickerPosition] = useState({
    top: 0,
    left: 0,
  });

  const updateTableCellNode = useCallback(() => {
    const selection = $getSelection();

    if ($isTableSelection(selection)) {
      const nodes = selection.getNodes();
      const cells = nodes
        .map((node) => $getTableCellNodeFromLexicalNode(node))
        .filter(
          (cell): cell is TableCellNode =>
            cell !== null && $isTableCellNode(cell),
        );

      if (cells.length > 0) {
        setTableCellNode(cells[0]);
        setSelectedCells(cells);
        return;
      }
    }

    if (!$isRangeSelection(selection)) {
      setTableCellNode(null);
      setSelectedCells([]);
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const cellNode = $getTableCellNodeFromLexicalNode(anchorNode);

    const isValidCellNode = cellNode !== null && $isTableCellNode(cellNode);
    if (!isValidCellNode) {
      setTableCellNode(null);
      setSelectedCells([]);
      return;
    }

    setTableCellNode(cellNode);
    setSelectedCells([cellNode]);
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateTableCellNode();
      });
    });
  }, [editor, updateTableCellNode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideColorPicker =
        colorPickerRef.current && !colorPickerRef.current.contains(target);
      const isOutsideButton =
        colorPickerButtonRef.current &&
        !colorPickerButtonRef.current.contains(target);

      if (isOutsideColorPicker && isOutsideButton) {
        setShowColorPicker(false);
      }
    };

    if (!showColorPicker) {
      return undefined;
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        if (!tableCellNode) {
          return;
        }
        $insertTableRowAtSelection(shouldInsertAfter);
      });
    },
    [editor, tableCellNode],
  );

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        if (!tableCellNode) {
          return;
        }
        $insertTableColumnAtSelection(shouldInsertAfter);
      });
    },
    [editor, tableCellNode],
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      if (!tableCellNode) {
        return;
      }
      $deleteTableRowAtSelection();
    });
  }, [editor, tableCellNode]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      if (!tableCellNode) {
        return;
      }
      $deleteTableColumnAtSelection();
    });
  }, [editor, tableCellNode]);

  const deleteTable = useCallback(() => {
    editor.update(() => {
      if (!tableCellNode) {
        return;
      }
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();
    });
  }, [editor, tableCellNode]);

  const setCellBackgroundColor = useCallback(
    (color: string, shouldClose = true) => {
      editor.update(() => {
        selectedCells.forEach((cellNode) => {
          const writable = cellNode.getWritable();
          writable.setBackgroundColor(color);
        });
      });

      if (shouldClose) {
        setShowColorPicker(false);
      }
    },
    [editor, selectedCells],
  );

  const handleCustomColorChange = useCallback(
    (color: string) => {
      setCellBackgroundColor(color, false);
    },
    [setCellBackgroundColor],
  );

  const handleColorPickerClose = useCallback(() => {
    setShowColorPicker(false);
  }, []);

  const handleToggleColorPicker = useCallback(() => {
    if (!showColorPicker && colorPickerButtonRef.current) {
      const rect = colorPickerButtonRef.current.getBoundingClientRect();
      setColorPickerPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setShowColorPicker((prev) => !prev);
  }, [showColorPicker]);

  if (!tableCellNode) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => insertTableRowAtSelection(false)}
        className="lexical-toolbar-button"
        aria-label={richTextEditorStrings.tableActionMenu.insertRowAbove}
        title={richTextEditorStrings.tableActionMenu.insertRowAbove}
      >
        <i className="pi pi-arrow-up" />
      </button>
      <button
        type="button"
        onClick={() => insertTableRowAtSelection(true)}
        className="lexical-toolbar-button"
        aria-label={richTextEditorStrings.tableActionMenu.insertRowBelow}
        title={richTextEditorStrings.tableActionMenu.insertRowBelow}
      >
        <i className="pi pi-arrow-down" />
      </button>
      <button
        type="button"
        onClick={() => insertTableColumnAtSelection(false)}
        className="lexical-toolbar-button"
        aria-label={richTextEditorStrings.tableActionMenu.insertColumnLeft}
        title={richTextEditorStrings.tableActionMenu.insertColumnLeft}
      >
        <i className="pi pi-arrow-left" />
      </button>
      <button
        type="button"
        onClick={() => insertTableColumnAtSelection(true)}
        className="lexical-toolbar-button"
        aria-label={richTextEditorStrings.tableActionMenu.insertColumnRight}
        title={richTextEditorStrings.tableActionMenu.insertColumnRight}
      >
        <i className="pi pi-arrow-right" />
      </button>
      <span className="lexical-toolbar-divider" />
      <button
        type="button"
        onClick={deleteTableRowAtSelection}
        className="lexical-toolbar-button"
        aria-label={richTextEditorStrings.tableActionMenu.deleteRow}
        title={richTextEditorStrings.tableActionMenu.deleteRow}
      >
        <i className="pi pi-trash" />
      </button>
      <button
        type="button"
        onClick={deleteTableColumnAtSelection}
        className="lexical-toolbar-button"
        aria-label={richTextEditorStrings.tableActionMenu.deleteColumn}
        title={richTextEditorStrings.tableActionMenu.deleteColumn}
      >
        <i className="pi pi-minus" />
      </button>
      <span className="lexical-toolbar-divider" />
      <span style={{ position: 'relative' }}>
        <button
          ref={colorPickerButtonRef}
          type="button"
          onClick={handleToggleColorPicker}
          className={`lexical-toolbar-button ${showColorPicker ? 'active' : ''}`}
          aria-label={richTextEditorStrings.tableActionMenu.cellBackgroundColor}
          title={richTextEditorStrings.tableActionMenu.cellBackgroundColor}
        >
          <i className="pi pi-palette" />
        </button>

        {showColorPicker && (
          <div
            ref={colorPickerRef}
            className="lexical-color-picker-popover"
            style={{
              position: 'fixed',
              top: `${colorPickerPosition.top}px`,
              left: `${colorPickerPosition.left}px`,
            }}
            role="dialog"
            aria-label={richTextEditorStrings.colorPicker.dialog.label}
          >
            <div className="lexical-color-picker-content">
              <h4 className="lexical-color-picker-title">
                {richTextEditorStrings.colorPicker.title}
              </h4>
              <div className="lexical-color-picker-grid">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className="lexical-color-picker-swatch"
                    style={{ backgroundColor: color.value }}
                    onClick={() => setCellBackgroundColor(color.value)}
                    aria-label={richTextEditorStrings.colorPicker.setColorLabel(
                      color.name,
                    )}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="lexical-color-picker-custom">
                <label
                  htmlFor="custom-bg-color"
                  className="lexical-color-picker-label"
                >
                  {richTextEditorStrings.colorPicker.custom.label}
                </label>
                <input
                  id="custom-bg-color"
                  type="color"
                  className="lexical-color-picker-input"
                  onChange={(e) => handleCustomColorChange(e.target.value)}
                  aria-label={
                    richTextEditorStrings.colorPicker.custom.ariaLabel
                  }
                />
              </div>
              <div className="lexical-color-picker-actions">
                <Button
                  type="button"
                  onClick={() => setCellBackgroundColor('')}
                  label={richTextEditorStrings.colorPicker.clear}
                  outlined
                  size="small"
                  style={{ flex: 1 }}
                />
                <Button
                  type="button"
                  onClick={handleColorPickerClose}
                  label={richTextEditorStrings.colorPicker.done}
                  size="small"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </div>
        )}
      </span>
      <span className="lexical-toolbar-divider" />
      <button
        type="button"
        onClick={deleteTable}
        className="lexical-toolbar-button"
        aria-label={richTextEditorStrings.tableActionMenu.deleteTable}
        title={richTextEditorStrings.tableActionMenu.deleteTable}
      >
        <i className="pi pi-times-circle" />
      </button>
    </>
  );
}
